import React from 'react';
import { Edit3, Heart, Gift, Clock } from 'react-feather';
import {
  InlineShell,
  InlineEyebrow,
  CardButton,
  hashHue,
  type F8CardData,
  type OnAction,
} from './cardHelpers';

function formatBirthday(input: any): string {
  if (!input) return '';
  if (typeof input === 'object' && input !== null) {
    if (input.day && input.month) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const m = parseInt(String(input.month), 10);
      const d = parseInt(String(input.day), 10);
      return isNaN(m) || isNaN(d) ? '' : `${monthNames[m - 1]} ${d}`;
    }
  }
  const d = input?.toDate ? input.toDate() : new Date(input);
  if (isNaN(d.getTime())) return String(input);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatLastInteraction(input: any): string {
  if (!input) return '';
  const d = input?.toDate ? input.toDate() : new Date(input);
  if (isNaN(d.getTime())) return '';
  const diffDays = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 30) return `${diffDays} days ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
}

export default function ContactInfoCard({
  card,
  onAction,
}: {
  card: F8CardData;
  onAction?: OnAction;
}) {
  const data = card?.data || {};
  const name: string = data.name || '';
  const relationship: string = data.relationship || data.contactType || '';
  const birthday = formatBirthday(data.birthday);
  const frequency: string = data.contactFrequency || '';
  const lastInteraction = formatLastInteraction(data.lastInteraction);
  const initial = (name[0] || '?').toUpperCase();
  const hue = hashHue(name);

  const heroStyle: React.CSSProperties = {
    background: `linear-gradient(135deg,
      oklch(0.50 0.22 ${hue}) 0%,
      oklch(0.55 0.24 ${(hue + 30) % 360}) 50%,
      oklch(0.65 0.26 ${(hue + 60) % 360}) 100%)`,
  };

  return (
    <InlineShell>
      <InlineEyebrow label="contact" trailing={relationship || undefined} />

      <div className="f8c-contact-head">
        <div className="f8c-contact-hero" style={heroStyle}>
          <span className="f8c-contact-initial">{initial}</span>
        </div>
        <div className="f8c-contact-identity">
          <div className="f8c-contact-name">{name || 'Unknown contact'}</div>
        </div>
      </div>

      {(birthday || frequency || lastInteraction) && (
        <div className="f8c-contact-meta-grid">
          {birthday && (
            <div className="f8c-contact-meta-item">
              <Gift size={14} color="#f0abfc" />
              <div>
                <div className="f8c-contact-meta-label">Birthday</div>
                <div className="f8c-contact-meta-value">{birthday}</div>
              </div>
            </div>
          )}
          {frequency && (
            <div className="f8c-contact-meta-item">
              <Heart size={14} color="#f0abfc" />
              <div>
                <div className="f8c-contact-meta-label">Frequency</div>
                <div className="f8c-contact-meta-value">{frequency}</div>
              </div>
            </div>
          )}
          {lastInteraction && (
            <div className="f8c-contact-meta-item">
              <Clock size={14} color="#f0abfc" />
              <div>
                <div className="f8c-contact-meta-label">Last seen</div>
                <div className="f8c-contact-meta-value">{lastInteraction}</div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="f8c-contact-actions">
        <CardButton
          primary
          onClick={() =>
            onAction?.({
              action: 'skill_invoke',
              params: { message: `draft a message to ${name}` },
            })
          }
        >
          <Edit3 size={13} />
          Draft message
        </CardButton>
        <CardButton
          onClick={() =>
            onAction?.({
              action: 'skill_invoke',
              params: { message: `give me tips for connecting with ${name}` },
            })
          }
        >
          <Heart size={13} />
          Tips
        </CardButton>
      </div>
    </InlineShell>
  );
}
